import React, { useEffect, useRef, useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import {
  formatMoneyForInput,
  isMoneyInputIntermediate,
  parseMoney,
} from '../utils/parsers';
import { validateMoney } from '../utils/validators';

export interface MoneyFieldProps {
  label: string;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  allowNegative?: boolean;
  min?: number;
  max?: number;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  showCurrencyAdornment?: boolean;
}

export function MoneyField({
  label,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  allowNegative = false,
  min,
  max,
  error: errorProp,
  helperText: helperTextProp,
  size = 'small',
  fullWidth = true,
  showCurrencyAdornment = true,
}: MoneyFieldProps): React.ReactElement {
  const [inputStr, setInputStr] = useState<string>(
    () => formatMoneyForInput(value)
  );
  const touched = useRef(false);
  const [, forceRender] = useState(0);

  // Re-seed when value prop changes from outside
  useEffect(() => {
    setInputStr(formatMoneyForInput(value));
  }, [value]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const raw = e.target.value;
    if (!isMoneyInputIntermediate(raw, allowNegative)) return;
    setInputStr(raw);
    onChange(parseMoney(raw, allowNegative));
  }

  function handleBlur(): void {
    touched.current = true;
    forceRender((n) => n + 1);
    onBlur?.();
  }

  function handleWheel(e: React.WheelEvent<HTMLDivElement>): void {
    (e.target as HTMLElement).blur();
  }

  const parsedValue = parseMoney(inputStr, allowNegative);

  let computedError = false;
  let computedHelperText = '';

  if (errorProp !== undefined) {
    computedError = errorProp;
    computedHelperText = helperTextProp ?? '';
  } else if (touched.current) {
    const msg = validateMoney(parsedValue, {
      required,
      allowNegative,
      min,
      max,
      fieldLabel: label,
    });
    computedError = msg !== '';
    computedHelperText = msg;
  }

  return (
    <TextField
      label={label}
      value={inputStr}
      onChange={handleChange}
      onBlur={handleBlur}
      onWheel={handleWheel}
      disabled={disabled}
      required={required}
      size={size}
      fullWidth={fullWidth}
      error={computedError}
      helperText={computedHelperText}
      inputProps={{ inputMode: 'decimal' }}
      type="text"
      InputProps={{
        startAdornment: showCurrencyAdornment ? (
          <InputAdornment position="start" sx={{ ml: 1 }}>
            $
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}